import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import TileCard from '../components/TileCard';

/**
 * ShiftPickupTile — driver opt-in for working a night (or day) off.
 *
 * States:
 *   scheduled      → plain confirmation of tonight's shift, no action
 *   off, no pickup → "Pick Up This Shift" button (two-tap confirm —
 *                    an accidental tap would text the admins)
 *   pickup active  → "You're ON" + cancel link
 *
 * The pickup applies to ONE shift window only and expires on its own;
 * all logic lives server-side (shiftPickupService).
 */
export default function ShiftPickupTile() {
  const [state, setState] = useState({ loading: true, status: null, error: null });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const status = await api.getShiftPickupStatus();
      setState({ loading: false, status, error: null });
    } catch (e) {
      setState({ loading: false, status: null, error: e.message });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doPickup = async () => {
    setBusy(true);
    try {
      const status = await api.pickupShift();
      setState({ loading: false, status, error: null });
    } catch (e) {
      setState(s => ({ ...s, error: e.message }));
    }
    setConfirming(false);
    setBusy(false);
  };

  const doCancel = async () => {
    if (!window.confirm('Turn off notifications? You will stop receiving dispatch calls.')) return;
    setBusy(true);
    try {
      const status = await api.cancelShiftPickup();
      setState({ loading: false, status, error: null });
    } catch (e) {
      setState(s => ({ ...s, error: e.message }));
    }
    setBusy(false);
  };

  const { loading, status, error } = state;

  return (
    <TileCard title="Notifications" loading={loading} error={error}>
      {status && (
        <div className="dash-shift-pickup">
          <div className="dash-shift-pickup-label">{status.role_label}</div>
          <div className="dash-shift-pickup-date">{status.shift_label}</div>

          {status.scheduled ? (
            <div className="dash-shift-pickup-state on-schedule">
              ✓ Notifications on — you're on the schedule
            </div>
          ) : status.pickup_active ? (
            <>
              <div className="dash-shift-pickup-state picked-up">
                ✓ Notifications ON — you'll get dispatch calls
              </div>
              <button
                type="button"
                className="dash-shift-pickup-cancel"
                onClick={doCancel}
                disabled={busy}
              >
                Turn off
              </button>
            </>
          ) : confirming ? (
            <>
              <div className="dash-shift-pickup-state off">
                Get dispatch calls for these hours? One time only — turns off automatically.
              </div>
              <div className="dash-shift-pickup-actions">
                <button
                  type="button"
                  className="dash-shift-pickup-btn"
                  onClick={doPickup}
                  disabled={busy}
                >
                  {busy ? 'Turning on…' : 'Yes, turn on'}
                </button>
                <button
                  type="button"
                  className="dash-shift-pickup-cancel"
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                >
                  Never mind
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="dash-shift-pickup-state off">
                Notifications are off
              </div>
              <button
                type="button"
                className="dash-shift-pickup-btn"
                onClick={() => setConfirming(true)}
              >
                Turn on Notifications
              </button>
            </>
          )}
        </div>
      )}
    </TileCard>
  );
}
